import React, { useCallback, useEffect } from 'react';

import { Disclaimer } from '@vw-marketing/us-components';

import { DisclaimerConfigurationContext } from '../../hooks/use-disclaimer-configuration';
import { DisclaimerKeys } from '../../utils/disclaimer';
import { InventoryDisclaimer, InventoryDisclaimers } from '../../types/main';
import { useDisclaimerReference } from '../../hooks/use-disclaimer-reference';

interface DisclaimerProcessorProps {
  disclaimers?: InventoryDisclaimers;
  children: React.ReactNode;
}

interface DisclaimerRegistrarProps {
  disclaimer: Disclaimer & { pageName?: string };
  onResolved: (token: string, reference: string) => void;
}

/**
 * Registers one disclaimer with the disclaimer-manager (no scope = shared
 * global scope). The manager assigns a sequential reference number that
 * accounts for disclaimers registered by other feature apps on the same page.
 * The assigned number is written back into context via onResolved.
 */
const DisclaimerRegistrar: React.FC<DisclaimerRegistrarProps> = ({
  disclaimer,
  onResolved,
}) => {
  const reference = useDisclaimerReference(disclaimer.description);

  useEffect(() => {
    if (reference && disclaimer.token) {
      onResolved(disclaimer.token, reference);
    }
  }, [reference, disclaimer.token]);

  return null;
};

export const DisclaimerProcessor: React.FC<DisclaimerProcessorProps> = ({
  disclaimers,
  children,
}) => {
  const [processedDisclaimers, setProcessedDisclaimers] = React.useState<
    (Disclaimer & { pageName?: string })[]
  >([]);

  useEffect(() => {
    if (!disclaimers) {
      setProcessedDisclaimers([]);
      return;
    }

    const pageKeys = [
      DisclaimerKeys.LandingKey,
      DisclaimerKeys.ResultsKey,
      DisclaimerKeys.DetailsKey,
    ];

    const allDisclaimersToProcess: {
      disclaimer: InventoryDisclaimer;
      pageKey: string;
    }[] = [];

    pageKeys.forEach((pageKey) => {
      disclaimers[pageKey]?.forEach((disclaimer) => {
        allDisclaimersToProcess.push({ disclaimer, pageKey });
      });
    });

    // number starts undefined — DisclaimerRegistrar fills it in with the
    // manager-assigned reference once registration completes
    setProcessedDisclaimers(
      allDisclaimersToProcess.map(({ disclaimer, pageKey }) => ({
        ...disclaimer,
        token: disclaimer.key,
        pageName: pageKey,
      })),
    );
  }, [disclaimers]);

  const handleResolved = useCallback(
    (token: string, reference: string) => {
      setProcessedDisclaimers((prev) =>
        prev.map((d) => (d.token === token ? { ...d, number: reference } : d)),
      );
    },
    [],
  );

  const disclaimersConfig = { disclaimers: processedDisclaimers };

  return (
    <DisclaimerConfigurationContext.Provider value={disclaimersConfig}>
      {processedDisclaimers.map((disclaimer) => (
        <DisclaimerRegistrar
          key={disclaimer.token}
          disclaimer={disclaimer}
          onResolved={handleResolved}
        />
      ))}
      {children}
    </DisclaimerConfigurationContext.Provider>
  );
};
